import React, { useState } from 'react';
import {
  ScrollView,
  View,
  useWindowDimensions,
  StyleSheet,
  Keyboard,
  TouchableOpacity,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import Text from '../../../components/Text';
import * as yup from 'yup';
import Button from '../../../components/Button';
import Heading from '../../../components/Heading';
import HStack from '../../../components/HStack';
import Input from '../../../components/Input';
import { yupResolver } from '@hookform/resolvers/yup'
import { useDispatch, useSelector } from 'react-redux';
import RegisterActionBar from '../../../components/RegisterActionBar';
import { RootState } from '../../../redux/reducers';
import { RegistrationData } from '../types';
import { setRegisterData } from '../registerReducer';
import ScreenWrapper from '../components/ScreenWrapper';
import isWeb from '../../../utils/isWeb';
import { useForm } from 'react-hook-form';
import TextInput from '../../../components/TextInput';
import Checkbox from '../../../components/Checkbox';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import formatISOToDisplayDate from '../../../utils/formatISOToDisplayDate';

const firstNameValidationSchema = yup.object().shape({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
});

const Name = ({ navigation }) => {
  const dispatch = useDispatch();
  const { firstName, lastName, dlNumber, addressLine1, driversLicenseExpiry, dateOfBirth, driversLicenseImage, doNotBuildFaceTemplate: _doNotBuildFaceTemplate } =
    useSelector((state: RootState) => state?.register);
console.log('👤 Name Screen - Current registration data from Redux:', {
  firstName,
  lastName,
  dlNumber,
  addressLine1,
  driversLicenseExpiry,
  dateOfBirth,
  driversLicenseImage,
  doNotBuildFaceTemplate: _doNotBuildFaceTemplate,
});
  const [doNotBuildFaceTemplate, setDoNotBuildFaceTemplate] = useState(_doNotBuildFaceTemplate || false);
  const [showDLDialog, setShowDLDialog] = useState(false);

  const { control, handleSubmit } = useForm({
    defaultValues: {
      firstName,
      lastName,
      //@ts-ignore
      dlNumber,
      addressLine1
    },
    resolver: yupResolver(firstNameValidationSchema)
  })
  const { width } = useWindowDimensions();

  const handleDetailsSubmit = (values: {
    firstName: string;
    lastName: string;
    addressLine1: string;
    dlNumber: string;
  }) => {
    const registrationData: RegistrationData = {
      firstName: values.firstName,
      lastName: values.lastName,
      addressLine1: values.addressLine1,
      dlNumber: values.dlNumber,
      driversLicenseExpiry: driversLicenseExpiry,
      doNotBuildFaceTemplate: doNotBuildFaceTemplate,
    };
    // @ts-ignore
    dispatch(setRegisterData(registrationData));
    navigation.navigate('Photo Capture');
  };

  const onError = () => {
    Keyboard.dismiss();
  }

  return (
    <ScreenWrapper alignTop>
      <SafeAreaView
        style={[
          styles.safeAreaView,
          isWeb && { backgroundColor: '#ffffff', width: width / 3 },
        ]}>
        <RegisterActionBar step={6} />
        <ScrollView keyboardShouldPersistTaps="handled">
          <View style={styles.container}>
            <Heading size="lg">Personal details </Heading>
            <Text style={styles.description}>
              Please look over all information to make sure it is accurate.
            </Text>
            <TouchableOpacity
              style={styles.checkboxContainer}
              onPress={() => {
                const newValue = !doNotBuildFaceTemplate;
                if (newValue && !driversLicenseImage) {
                  setShowDLDialog(true);
                  return;
                }
                setDoNotBuildFaceTemplate(newValue);
              }}
              activeOpacity={0.7}>
              <Text style={styles.checkboxLabel}>
                Opt out of facial recognition
              </Text>
              <Checkbox
                value={doNotBuildFaceTemplate ? 'true' : 'false'}
                isChecked={doNotBuildFaceTemplate}
                onChange={value => {
                  if (value && !driversLicenseImage) {
                    setShowDLDialog(true);
                    return;
                  }
                  setDoNotBuildFaceTemplate(value);
                }}
                aria-label="Do not build face template"
              />
            </TouchableOpacity>
            <TextInput
              //@ts-ignore
              control={control}
              name="firstName"
              label="First name *"
            />
            <TextInput
              //@ts-ignore
              control={control}
              name="lastName"
              label="Last name *"
            />
            <TextInput
              //@ts-ignore
              control={control}
              name="dlNumber"
              label="Driver's license number"
            />
            <View style={styles.inputContainer}>
              <Text style={styles.label}>Birth date </Text>
              <Input style={styles.input} value={formatISOToDisplayDate(dateOfBirth) || ''} />
            </View>
            <View style={styles.inputContainer}>
              <Text style={styles.label}>Driver's license expiry date</Text>
              <Input style={styles.input} value={formatISOToDisplayDate(driversLicenseExpiry) || ''} />
            </View>
            <TextInput
              //@ts-ignore
              control={control}
              name="addressLine1"
              label="Address"
            />
          </View>
        </ScrollView>
        <HStack style={styles.buttonContainer}>
          <Button
            variant="outline"
            py="4"
            onPress={() => {
              navigation.goBack();
            }
            }
            flex={1}
            mr={2}>
            Previous
          </Button>
          <Button
            onPress={handleSubmit(handleDetailsSubmit, onError)}
            style={styles.button}>
            Next
          </Button>
        </HStack>
      </SafeAreaView>
      <ConfirmationDialog
        isOpen={showDLDialog}
        onClose={() => setShowDLDialog(false)}
        onNoPressed={() => setShowDLDialog(false)}
        onYesPressed={() => {
          setShowDLDialog(false);
          navigation.goBack();
        }}
        title="Driver's License Required"
        description="Driver's License required to opt out of facial recognition. This is how we know it is you who is opting out."
        negativeLabel="Cancel"
        positiveLabel="Upload DL"
      />
    </ScreenWrapper>
  );
};

const styles = StyleSheet.create({
  safeAreaView: {
    flex: 1,
    marginVertical: 16,
  },
  container: {
    marginHorizontal: 24,
    marginTop: 16,
  },
  description: {
    marginTop: 32,
    fontSize: 18,
    marginBottom: 16,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 8,
    marginBottom: 16,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
  },
  checkboxLabel: {
    fontSize: 16,
    flex: 1,
    marginRight: 12,
  },
  inputContainer: {
    marginBottom: 24,
  },
  label: {
  },
  input: {
    marginTop: 8,
  },
  buttonContainer: {
    margin: 24,
    marginTop: 32,
  },
  button: {
    flex: 1,
  },
});

export default Name;
